import PageLayout from '@/components/PageLayout';
import { ArrowLeft, Mail, Linkedin, Shield, Terminal, Target, Lock, Users, Zap, Award, ArrowRight, Globe, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from "framer-motion";
import { useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import SEO from '@/components/SEO';

const Careers = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  
  return (
    <PageLayout>
      <SEO 
        title="Cybersecurity Careers | Join CIPHER Security's Expert Team"
        description="Join CIPHER Security's elite cybersecurity team. Career opportunities for penetration testers, DevSecOps engineers, security analysts, and cybersecurity consultants. Remote-friendly positions with leading cybersecurity experts."
        keywords={[
          'cybersecurity careers',
          'cybersecurity jobs',
          'penetration testing jobs',
          'DevSecOps careers',
          'security analyst positions',
          'cybersecurity consultant jobs',
          'remote cybersecurity jobs',
          'application security careers',
          'cloud security jobs',
          'incident response careers',
          'security engineering jobs',
          'cybersecurity hiring'
        ]}
      />
      
      {/* Hero Section */}
      <section
        className="pt-24 pb-16 relative overflow-hidden"
        style={{
          background: "#0f0f17",
          color: "#fff",
        }}
      >
        {/* Animated background gradient */}
        <div className="absolute inset-0 overflow-hidden z-0">
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 30% 30%, rgba(0, 168, 107, 0.15) 0%, transparent 50%)" }} />
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 70% 70%, rgba(6, 182, 212, 0.15) 0%, transparent 50%)" }} />
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 50%, rgba(251, 191, 36, 0.1) 0%, transparent 70%)" }} />
        </div>

        <div className="container mx-auto px-4 sm:px-8 relative z-10" style={{ maxWidth: 1280 }}>
          <div className="max-w-6xl mx-auto">
            <Link
              to="/"
              className="inline-flex items-center mb-8 transition-colors group rounded-full px-5 py-2 font-semibold shadow-lg"
              style={{
                background: "linear-gradient(135deg, #00a86b 0%, #06b6d4 100%)",
                color: "#fff",
                fontWeight: 600,
                fontSize: 16,
                borderRadius: "999px",
                boxShadow: "0 4px 24px 0 rgba(0, 168, 107, 0.18)"
              }}
            >
              <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
              Back to Home
            </Link>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h1
                className="mb-6"
                style={{
                  fontSize: "clamp(2rem, 6vw, 3.75rem)",
                  fontWeight: 700,
                  lineHeight: 1.1,
                  color: "#fff",
                  letterSpacing: "-0.025em",
                }}
              >
                <span
                  className="block"
                  style={{
                    background: "linear-gradient(135deg, #00a86b 0%, #06b6d4 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    color: "#00a86b",
                    fontWeight: 700,
                  }}
                >
                  Career Excellence
                </span>
                <span className="block mt-2 text-base sm:text-lg md:text-2xl" style={{ color: "#e5e7eb" }}>
                  Join Our Elite Cybersecurity Team
                </span>
              </h1>
              <p
                className="max-w-3xl mx-auto text-base sm:text-lg px-4 sm:px-0"
                style={{
                  color: "#d1d5db",
                  lineHeight: 1.7,
                  fontWeight: 400,
                }}
              >
                Join our elite team of cybersecurity subject matter experts. We're building the future of cyber risk management with professionals who excel in specialized security domains and deliver measurable results.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section
        className="py-16 px-4 sm:px-8"
        style={{
          background: "#0f0f17",
        }}
      >
        <div className="container mx-auto" style={{ maxWidth: 1280 }}>
          <div className="max-w-6xl mx-auto">
            {/* Career Opportunities & Culture */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.8, 
                  delay: 0,
                  ease: [0.16, 1, 0.3, 1]
                }}
              >
                <Card
                  className="h-full group relative overflow-hidden"
                  style={{
                    background: "rgba(6, 182, 212, 0.08)",
                    backdropFilter: "blur(8px)",
                    border: "1px solid rgba(6, 182, 212, 0.30)",
                    borderRadius: 16,
                    padding: 0,
                    transition: "all 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
                    transformStyle: "preserve-3d",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(6, 182, 212, 0.15)";
                    e.currentTarget.style.borderColor = "#06b6d4";
                    e.currentTarget.style.boxShadow = "0 20px 40px rgba(6, 182, 212, 0.20)";
                    e.currentTarget.style.transform = "translateY(-8px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(6, 182, 212, 0.08)";
                    e.currentTarget.style.borderColor = "rgba(6, 182, 212, 0.30)";
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.transform = "translateY(0px)";
                  }}
                >
                  <CardContent className="p-6 sm:p-8">
                    <div className="flex flex-col sm:flex-row items-start mb-6">
                      <div
                        className="flex items-center justify-center mb-4 sm:mb-0 sm:mr-4"
                        style={{
                          width: 64,
                          height: 64,
                          padding: 16,
                          borderRadius: 12,
                          backgroundColor: "#06b6d420",
                          color: "#06b6d4",
                          marginBottom: 24,
                          boxShadow: "0 4px 16px #06b6d420",
                          transition: "transform 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "scale(1.1)";
                          e.currentTarget.style.filter = "drop-shadow(0 0 20px #06b6d4)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                          e.currentTarget.style.filter = "none";
                        }}
                      >
                        <Users className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h3
                          className="text-xl sm:text-2xl"
                          style={{
                            fontWeight: 700,
                            color: "#fff",
                            marginBottom: 8,
                          }}
                        >
                          Career Opportunities
                        </h3>
                      </div>
                    </div>
                    <p
                      className="text-sm sm:text-base"
                      style={{
                        color: "#d1d5db",
                        lineHeight: 1.6,
                        marginBottom: 16,
                      }}
                    >
                      We're seeking exceptional talent across multiple disciplines:
                    </p>
                    <div className="grid grid-cols-1 gap-2 sm:gap-3 mb-6">
                      {[
                        "Security Analysts & Risk Management",
                        "Offensive Security Testing",
                        "DevSecOps Engineers & Cloud Security", 
                        "Compliance & Infrastructure Security",
                        "Incident Response & Threat Intelligence",
                        "Leadership & Business Development"
                      ].map((feature, idx) => (
                        <div
                          key={idx}
                          className="flex items-center text-xs sm:text-sm"
                          style={{
                            color: "#9ca3af",
                          }}
                        >
                          <div
                            style={{
                              width: 6,
                              height: 6,
                              background: "#06b6d4",
                              borderRadius: "50%",
                              marginRight: 8,
                              flexShrink: 0,
                            }}
                          />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.8, 
                  delay: 0.1,
                  ease: [0.16, 1, 0.3, 1]
                }}
              >
                <Card
                  className="h-full group relative overflow-hidden"
                  style={{
                    background: "rgba(168, 85, 247, 0.08)",
                    backdropFilter: "blur(8px)",
                    border: "1px solid rgba(168, 85, 247, 0.30)",
                    borderRadius: 16,
                    padding: 0,
                    transition: "all 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
                    transformStyle: "preserve-3d",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(168, 85, 247, 0.15)";
                    e.currentTarget.style.borderColor = "#a855f7";
                    e.currentTarget.style.boxShadow = "0 20px 40px rgba(168, 85, 247, 0.20)";
                    e.currentTarget.style.transform = "translateY(-8px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(168, 85, 247, 0.08)";
                    e.currentTarget.style.borderColor = "rgba(168, 85, 247, 0.30)";
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.transform = "translateY(0px)";
                  }}
                >
                  <CardContent className="p-6 sm:p-8">
                    <div className="flex flex-col sm:flex-row items-start mb-6">
                      <div
                        className="flex items-center justify-center mb-4 sm:mb-0 sm:mr-4"
                        style={{
                          width: 64,
                          height: 64,
                          padding: 16,
                          borderRadius: 12,
                          backgroundColor: "#a855f720",
                          color: "#a855f7",
                          marginBottom: 24,
                          boxShadow: "0 4px 16px #a855f720",
                          transition: "transform 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "scale(1.1)";
                          e.currentTarget.style.filter = "drop-shadow(0 0 20px #a855f7)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                          e.currentTarget.style.filter = "none";
                        }}
                      >
                        <Globe className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h3
                          className="text-xl sm:text-2xl"
                          style={{
                            fontWeight: 700,
                            color: "#fff",
                            marginBottom: 8,
                          }}
                        >
                          Our Culture
                        </h3>
                      </div>
                    </div>
                    <p
                      className="text-sm sm:text-base"
                      style={{
                        color: "#d1d5db",
                        lineHeight: 1.6,
                        marginBottom: 16,
                      }}
                    >
                      Our work environment and values:
                    </p>
                    <div className="grid grid-cols-1 gap-2 sm:gap-3 mb-6">
                      {[
                        "Remote Flexible Work Environment",
                        "Continuous Learning & Development",
                        "Industry-Leading Professionals",
                        "Real Impact in Cybersecurity",
                        "Exceptional Results Commitment",
                        "Cutting-Edge Tools & Innovation"
                      ].map((feature, idx) => (
                        <div
                          key={idx}
                          className="flex items-center text-xs sm:text-sm"
                          style={{
                            color: "#9ca3af",
                          }}
                        >
                          <div
                            style={{
                              width: 6,
                              height: 6,
                              background: "#a855f7",
                              borderRadius: "50%",
                              marginRight: 8,
                              flexShrink: 0,
                            }}
                          />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            {/* Why Work With Us */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mb-16"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center mb-12"
              >
                <h2
                  className="text-2xl sm:text-3xl md:text-4xl"
                  style={{
                    fontWeight: 700,
                    color: "#fff",
                    letterSpacing: "-0.01em",
                    marginBottom: 16,
                  }}
                >
                  Why Work With CIPHER
                </h2>
                <p
                  className="text-base sm:text-lg px-4 sm:px-0"
                  style={{
                    color: "#d1d5db",
                    maxWidth: 800,
                    margin: "0 auto",
                  }}
                >
                  Elite team, meaningful work, exceptional growth
                </p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.8, 
                  delay: 0.2,
                  ease: [0.16, 1, 0.3, 1]
                }}
              >
                <Card
                  className="group relative overflow-hidden"
                  style={{
                    background: "rgba(59, 130, 246, 0.08)",
                    backdropFilter: "blur(8px)",
                    border: "1px solid rgba(59, 130, 246, 0.30)",
                    borderRadius: 16,
                    padding: 0,
                    transition: "all 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
                    transformStyle: "preserve-3d",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(59, 130, 246, 0.15)";
                    e.currentTarget.style.borderColor = "#3b82f6";
                    e.currentTarget.style.boxShadow = "0 20px 40px rgba(59, 130, 246, 0.20)";
                    e.currentTarget.style.transform = "translateY(-8px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(59, 130, 246, 0.08)";
                    e.currentTarget.style.borderColor = "rgba(59, 130, 246, 0.30)";
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.transform = "translateY(0px)";
                  }}
                >
                  <CardContent className="p-6 sm:p-8">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                      <div className="lg:col-span-2 space-y-6">
                        <p
                          className="text-sm sm:text-base"
                          style={{
                            color: "#d1d5db",
                            lineHeight: 1.6,
                            marginBottom: 16,
                          }}
                        >
                          Join a boutique cybersecurity firm where your expertise matters. We're not a massive corporation where you're just a number - we're a focused team of senior cybersecurity professionals who deliver exceptional results for our clients.
                        </p>
                        <p
                          className="text-sm sm:text-base"
                          style={{
                            color: "#d1d5db",
                            lineHeight: 1.6,
                            marginBottom: 16,
                          }}
                        >
                          Every team member works directly with senior leadership and contributes meaningfully to our cybersecurity risk management methodology. We believe in empowering our people with the latest tools, continuous learning opportunities, and the autonomy to excel in their specialized domains.
                        </p>
                        <p
                          className="text-sm sm:text-base"
                          style={{
                            color: "#d1d5db",
                            lineHeight: 1.6,
                            marginBottom: 16,
                          }}
                        >
                          Whether you're joining as a security analyst, offensive security specialist, DevSecOps engineer, or compliance specialist, you'll be working on cutting-edge projects that make a real difference in protecting organizations from evolving cyber threats.
                        </p>
                      </div>
                      <div
                        style={{
                          background: "linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)",
                          borderRadius: 16,
                          padding: 24,
                          color: "#fff",
                          minWidth: 200,
                          boxShadow: "0 4px 24px 0 rgba(59, 130, 246, 0.15)",
                        }}
                      >
                        <h4
                          className="text-lg sm:text-xl"
                          style={{
                            fontWeight: 700,
                            color: "#fff",
                            marginBottom: 16,
                            letterSpacing: "0.04em",
                          }}
                        >
                          Benefits & Perks
                        </h4>
                        <div className="grid grid-cols-1 gap-3">
                          {[
                            "Remote Work Flexibility",
                            "Professional Development", 
                            "Industry Certifications",
                            "Cutting-Edge Technology",
                            "Meaningful Projects"
                          ].map((feature, idx) => (
                            <div
                              key={idx}
                              className="flex items-center text-xs sm:text-sm"
                              style={{
                                color: "#e5e7eb",
                              }}
                            >
                              <div
                                style={{
                                  width: 6,
                                  height: 6,
                                  background: "#fff",
                                  borderRadius: "50%",
                                  marginRight: 8,
                              flexShrink: 0,
                                }}
                              />
                              {feature}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>

            {/* Call to Action */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.8, 
                delay: 0.3,
                ease: [0.16, 1, 0.3, 1]
              }}
            >
              <Card
                className="text-center group relative overflow-hidden"
                style={{
                  background: "rgba(251, 191, 36, 0.08)",
                  backdropFilter: "blur(8px)",
                  border: "1px solid rgba(251, 191, 36, 0.30)",
                  borderRadius: 16,
                  padding: 0,
                  transition: "all 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
                  transformStyle: "preserve-3d",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(251, 191, 36, 0.15)";
                  e.currentTarget.style.borderColor = "#fbbf24";
                  e.currentTarget.style.boxShadow = "0 20px 40px rgba(251, 191, 36, 0.20)";
                  e.currentTarget.style.transform = "translateY(-8px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(251, 191, 36, 0.08)";
                  e.currentTarget.style.borderColor = "rgba(251, 191, 36, 0.30)";
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.transform = "translateY(0px)";
                }}
              >
                <CardContent style={{ padding: 32 }}>
                  <div
                    className="flex items-center justify-center mx-auto mb-6"
                    style={{
                      width: 64,
                      height: 64,
                      padding: 16,
                      borderRadius: 12,
                      backgroundColor: "#fbbf2420",
                      color: "#fbbf24",
                      boxShadow: "0 4px 16px #fbbf2420",
                      transition: "transform 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "scale(1.1)";
                      e.currentTarget.style.filter = "drop-shadow(0 0 20px #fbbf24)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                      e.currentTarget.style.filter = "none";
                    }}
                  >
                    <Users className="w-6 h-6" />
                  </div>
                  <h3
                    className="text-xl sm:text-2xl"
                    style={{
                      fontWeight: 700,
                      color: "#fff",
                      marginBottom: 8,
                    }}
                  >
                    Shape the Future of Cybersecurity
                  </h3>
                  <p
                    className="text-sm sm:text-base"
                    style={{
                      color: "#d1d5db",
                      lineHeight: 1.6,
                      marginBottom: 16,
                    }}
                  >
                    We're seeking exceptional security professionals who excel in their craft and deliver measurable impact. 
                    If you're ready to work with industry leaders on critical security challenges, we want to hear from you.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center mt-6">
                    <a
                      href="mailto:sales@ciphersecurity.io?subject=Career Application"
                      className="inline-flex items-center font-medium transition-all group-hover:translate-x-1"
                      style={{
                        color: "#fbbf24",
                        fontSize: 16,
                        fontWeight: 600,
                        textDecoration: "none",
                      }}
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Apply Now
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </a>
                    <Link
                      to="/about"
                      className="inline-flex items-center font-medium transition-all group-hover:translate-x-1"
                      style={{
                        color: "#fbbf24",
                        fontSize: 16,
                        fontWeight: 600,
                        textDecoration: "none",
                      }}
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      Learn About Us
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default Careers;